import { useMemo, useState, useEffect, useContext, useReducer, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import {
  useIssueColors,
  getRequestTypeColor,
  useIssueNavigation,
  useIssuesData,
  useLoadingComponents
} from '../hooks';
import useAnalytics from '../hooks/useAnalytics';
import { IssueCard } from './IssueCard';
import { getInterventionDate, getInterventionDay } from '../utils/interventionDate';
import { SkeletonList } from './SkeletonList';
import Modal from './Modal';
import AppContext from '../contexts/AppContext';
import { Select, Button, TextField } from '@radix-ui/themes';
import { MagnifyingGlassIcon } from '@radix-ui/react-icons';
import { isInWebView } from '../utils/deviceUtils';
import { hasRole, ROLES } from '../utils/roleUtils';
import { getDraftTabLabel } from '../utils/draftConfig';
import { pendingBadge, pendingBadgeLabel, useMyInterventionRequests } from '../hooks/useMyInterventionRequests';
import MyInterventionRequests from '../pages/MyInterventionRequests';
import MyAvailabilitySheet from '../pages/TechnicianProfile/MyAvailabilitySheet';
import { MultiSelect } from './ui/multi-select';
import { ActiveFilterChips } from './ActiveFilterChips';
import { IssuesTabs, withCount } from './IssuesTabs';
import {
  DEFAULT_FILTERS,
  availableBrands,
  filterChips,
  matchesBrands,
  readFilters,
  saveFilters,
  withSelected,
} from '../utils/issueListFilters';

const filterReducer = (state, action) => {
  switch (action.type) {
    case 'SET_TEMP_FILTER':
      return {
        ...state,
        temp: {
          ...state.temp,
          [action.field]: action.value
        }
      };
    case 'APPLY_FILTERS':
      return {
        ...state,
        active: { ...state.temp }
      };
    case 'RESET_FILTERS':
      return {
        ...state,
        active: DEFAULT_FILTERS,
        temp: DEFAULT_FILTERS
      };
    case 'CLEAR_FILTER':
      return {
        ...state,
        active: { ...state.active, [action.field]: DEFAULT_FILTERS[action.field] }
      };
    case 'OPEN_MODAL':
      return {
        ...state,
        temp: { ...state.active }
      };
    default:
      return state;
  }
};

const IssuesList = ({
  userRole,
  userName,
  userSectors,
  userServiceDeskName,
}) => {
  useAnalytics({
    currentUser: userName,
    project: userServiceDeskName,
    role: userRole,
    module: 'view-issues',
  });

  const { t } = useTranslation();
  const { serviceDesk, currentUser } = useContext(AppContext);
  const serviceDeskConfig = serviceDesk?.config;
  const { getStatusColor, getRequestTypeColor } = useIssueColors();
  const { handleNavigateToIssue } = useIssueNavigation();
  const { ErrorDisplay, EmptyState } = useLoadingComponents();
  const deviceIsInWebView = isInWebView();
  const [activeTab, setActiveTab] = useState('open');
  /**
   * The requests segment, for a technician only — strictly: this screen also serves `user`,
   * `admin`, `manager` and `superadmin`, and they have no requests of their own to read.
   * `?tab=requests` carries it, so the notification's deep link opens on it.
   */
  const isTechnician = hasRole(userRole, ROLES.TECHNICIAN);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab');
  const showRequests = isTechnician && tab === 'requests';
  const myRequests = useMyInterventionRequests(isTechnician);
  /**
   * A count of **state**, not of unread — requests still to answer (D28). It is the only figure
   * this screen carries, and the decision is deliberate: a technician's bell was built and
   * removed, because this number already tells him the one thing he has to act on. Amber while a
   * reminder is unseen (E15).
   */
  const requestsBadge = pendingBadge(myRequests.data);
  const pendingRequests = requestsBadge.pending;

  const handleSegmentChange = (segment) => {
    const params = new URLSearchParams(searchParams);
    params.delete('request');
    if (segment === 'requests') params.set('tab', 'requests');
    else params.delete('tab');
    setSearchParams(params);
  };

  const [ownerScope, setOwnerScope] = useState('mine');
  const [filterState, dispatchFilter] = useReducer(filterReducer, currentUser?.accountId, (accountId) => {
    const active = readFilters(accountId);
    return { active, temp: active };
  });
  useEffect(() => {
    saveFilters(currentUser?.accountId, filterState.active);
  }, [currentUser?.accountId, filterState.active]);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [tempTab, setTempTab] = useState('open');
  const [searchQuery, setSearchQuery] = useState('');
  const {
    issuesOpen,
    issuesDraft,
    issuesToValidate,
    issuesClosed,
    issuesArchived,
    loading,
    error,
    refetchOpen,
    refetchClosed,
    refetchArchived,
    refetchToValidate,
    refetchDraft,
  } = useIssuesData({ userRole, serviceDeskConfig });

  const allIssues = useMemo(() => [
    ...(issuesOpen || []),
    ...(issuesDraft || []),
    ...(issuesToValidate || []),
    ...(issuesClosed || []),
    ...(issuesArchived || [])
  ], [issuesOpen, issuesDraft, issuesToValidate, issuesClosed, issuesArchived]);

  // The technician list spans every enabled service desk, so a ticket's Jira
  // project name is the client. Single-desk users get a single-entry list.
  const availableClients = useMemo(() => {
    const clients = new Set();
    allIssues.forEach(issue => {
      const client = issue.fields?.project?.name;
      if (client) clients.add(client);
    });
    return withSelected(Array.from(clients).sort(), filterState.active.client);
  }, [allIssues, filterState.active.client]);

  const availablePriorities = useMemo(() => {
    const priorities = [];
    allIssues.forEach(issue => {
      if (issue.fields.priority.name && !priorities.includes(issue.fields.priority.name)) {
        priorities.push(issue.fields.priority.name);
      }
    });
    return withSelected(priorities, filterState.active.priority);
  }, [allIssues, filterState.active.priority]);

  const availableRequestTypes = useMemo(() => {
    const requestTypes = [];
    allIssues.forEach(issue => {
      if (issue.mappedFields.requestType?.requestType?.name && !requestTypes.includes(issue.mappedFields.requestType?.requestType?.name)) {
        requestTypes.push(issue.mappedFields.requestType?.requestType?.name);
      }
    });
    return withSelected(requestTypes, filterState.active.requestType);
  }, [allIssues, filterState.active.requestType]);

  const availableEnseignes = useMemo(() => {
    const enseignes = new Set();
    allIssues.forEach(issue => {
      const shopSign = issue.mappedFields?.shopSign;
      if (shopSign) {
        if (Array.isArray(shopSign)) {
          shopSign.forEach(sign => {
            if (sign?.value) enseignes.add(sign.value);
          });
        } else if (shopSign?.value) {
          enseignes.add(shopSign.value);
        }
      }
    });
    return withSelected(Array.from(enseignes).sort(), filterState.active.enseigne);
  }, [allIssues, filterState.active.enseigne]);

  const availableShops = useMemo(() => {
    const shops = new Set();
    allIssues.forEach(issue => {
      const interventionPoint = issue.mappedFields?.intervention_point;
      if (interventionPoint) {
        if (Array.isArray(interventionPoint)) {
          interventionPoint.forEach(point => {
            if (point?.value) shops.add(point.value);
          });
        } else if (interventionPoint?.value) {
          shops.add(interventionPoint.value);
        }
      }
    });
    return withSelected(Array.from(shops).sort(), filterState.active.shop);
  }, [allIssues, filterState.active.shop]);

  const brandOptions = useMemo(
    () => availableBrands(allIssues, filterState.active.brands),
    [allIssues, filterState.active.brands]
  );

  const isCreatedByMe = useCallback((issue) => {
    const reporter = issue.fields?.reporter;

    if (!reporter)
      return false;

    const myAccountId = currentUser?.accountId;
    const myEmail = currentUser?.email?.toLowerCase();

    if (myAccountId && reporter.accountId === myAccountId)
      return true;

    if (myEmail && reporter.emailAddress?.toLowerCase() === myEmail)
      return true;

    return false;
  }, [currentUser]);

  // Derive which owner kinds actually exist in the data, so the toggle is
  // only shown when both "mine" and "others" issues are present.
  const { hasMineIssues, hasOthersIssues } = useMemo(() => ({
    hasMineIssues: allIssues.some((issue) => isCreatedByMe(issue)),
    hasOthersIssues: allIssues.some((issue) => !isCreatedByMe(issue)),
  }), [allIssues, isCreatedByMe]);

  const showOwnerToggle = hasMineIssues && hasOthersIssues;

  // When a single kind exists, force that kind regardless of the selected tab
  // so we never render an empty list for an irrelevant ownerScope.
  const effectiveOwnerScope = showOwnerToggle ? ownerScope : (hasMineIssues ? 'mine' : 'others');

  const applyFilters = (issuesList) => {
    let filtered = [...issuesList];

    filtered = filtered.filter(issue => (effectiveOwnerScope === 'mine' ? isCreatedByMe(issue) : !isCreatedByMe(issue)));

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();

      filtered = filtered.filter(issue => {
        const titleMatch = String(issue.mappedFields?.title || '').toLowerCase().includes(query);
        const keyMatch = String(issue.key || '').toLowerCase().includes(query);
        const interventionPointValue = Array.isArray(issue.mappedFields?.intervention_point)
          ? issue.mappedFields?.intervention_point[0]?.value || ''
          : issue.mappedFields?.intervention_point?.value || '';
        const interventionPointMatch = interventionPointValue.toLowerCase().includes(query);
        const requestTypeMatch = (issue.mappedFields?.requestType?.requestType?.name || '').toLowerCase().includes(query);

        return titleMatch ||
          keyMatch ||
          interventionPointMatch ||
          requestTypeMatch;
      });
    }

    if (filterState.active.client) {
      filtered = filtered.filter(issue =>
        issue.fields?.project?.name === filterState.active.client
      );
    }

    if (filterState.active.priority) {
      filtered = filtered.filter(issue =>
        issue.fields?.priority?.name === filterState.active.priority
      );
    }

    if (filterState.active.requestType) {
      filtered = filtered.filter(issue =>
        issue.mappedFields?.requestType?.requestType?.name === filterState.active.requestType
      );
    }

    if (filterState.active.enseigne) {
      filtered = filtered.filter(issue => {
        const shopSign = issue.mappedFields?.shopSign;
        if (!shopSign) return false;

        if (Array.isArray(shopSign)) {
          return shopSign.some(sign => sign?.value === filterState.active.enseigne);
        }
        return shopSign?.value === filterState.active.enseigne;
      });
    }

    if (filterState.active.shop) {
      filtered = filtered.filter(issue => {
        const interventionPoint = issue.mappedFields?.intervention_point;
        if (!interventionPoint) return false;

        if (Array.isArray(interventionPoint)) {
          return interventionPoint.some(point => point?.value === filterState.active.shop);
        }
        return interventionPoint?.value === filterState.active.shop;
      });
    }

    if (filterState.active.duedate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(issue => {
        const dueDate = getInterventionDay(issue);
        if (!dueDate) return false;

        switch (filterState.active.duedate) {
          case 'today':
            return dueDate.getTime() === today.getTime();
          case 'thisWeek':
            const weekEnd = new Date(today);
            weekEnd.setDate(today.getDate() + (7 - today.getDay()));
            return dueDate.getTime() >= today.getTime() && dueDate.getTime() <= weekEnd.getTime();
          case 'thisMonth':
            const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            monthEnd.setHours(23, 59, 59, 999);
            return dueDate.getTime() >= today.getTime() && dueDate.getTime() <= monthEnd.getTime();
          case 'overdue':
            return dueDate.getTime() < today.getTime();
          default:
            return true;
        }
      });
    }

    filtered = filtered.filter((issue) => matchesBrands(issue, filterState.active.brands));

    return filtered;
  };

  const filteredIssuesOpen = useMemo(() => applyFilters(issuesOpen || []), [issuesOpen, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesDraft = useMemo(() => applyFilters(issuesDraft || []), [issuesDraft, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesToValidate = useMemo(() => applyFilters(issuesToValidate || []), [issuesToValidate, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesClosed = useMemo(() => applyFilters(issuesClosed || []), [issuesClosed, filterState.active, searchQuery, effectiveOwnerScope]);
  const filteredIssuesArchived = useMemo(() => applyFilters(issuesArchived || []), [issuesArchived, filterState.active, searchQuery, effectiveOwnerScope]);

  const tabs = [
    issuesDraft.length > 0 && { key: 'draft', label: getDraftTabLabel(t, serviceDeskConfig?.draft), count: filteredIssuesDraft.length },
    issuesToValidate.length > 0 && { key: 'toValidate', label: t('To validate'), count: filteredIssuesToValidate.length },
    { key: 'open', label: t('In progress'), count: filteredIssuesOpen.length },
    { key: 'closed', label: t('Closed'), count: filteredIssuesClosed.length },
    serviceDeskConfig?.issueValidation?.statusIdArchived && { key: 'archived', label: t('Archived'), count: filteredIssuesArchived.length },
  ].filter(Boolean);

  const currentTab = tabs.some(({ key }) => key === activeTab) ? activeTab : 'open';
  const defaultTab = issuesDraft.length > 0 ? 'draft' : 'open';
  const tabChip = currentTab === defaultTab ? undefined : { label: t('Status'), value: tabs.find(({ key }) => key === currentTab).label, onReset: () => handleTabChange(defaultTab) };

  const issues = useMemo(() => {
    let filteredIssues = currentTab === 'open' ? filteredIssuesOpen :
      (currentTab === 'draft' ? filteredIssuesDraft :
      (currentTab === 'toValidate' ? filteredIssuesToValidate :
      (currentTab === 'archived' ? filteredIssuesArchived :
      filteredIssuesClosed)));

    return filteredIssues.sort((a, b) => new Date(b.updated) - new Date(a.updated));
  }, [currentTab, filteredIssuesOpen, filteredIssuesDraft, filteredIssuesToValidate, filteredIssuesClosed, filteredIssuesArchived]);

  useEffect(() => {
    if (issuesDraft.length > 0) setActiveTab('draft');
  }, [issuesDraft]);

  const handleTabChange = (tab) => {
    if (tab === currentTab) return;
    setActiveTab(tab);
    if (tab === 'closed' && issuesClosed == null) refetchClosed();
    if (tab === 'archived' && issuesArchived == null) refetchArchived();
    if (tab === 'open' && issuesOpen == null) refetchOpen();
    if (tab === 'draft' && issuesDraft == null) refetchDraft();
    if (tab === 'toValidate' && issuesToValidate == null) refetchToValidate();
  };

  const isMultiSectors = userSectors[0] === '*' || userSectors.length > 1;

  const handleOpenFilterModal = () => {
    dispatchFilter({ type: 'OPEN_MODAL' });
    setTempTab(currentTab);
    setIsFilterModalOpen(true);
  };

  const handleApplyFilters = () => {
    dispatchFilter({ type: 'APPLY_FILTERS' });
    handleTabChange(tempTab);
    setIsFilterModalOpen(false);
  };

  const handleCancelFilters = () => {
    setIsFilterModalOpen(false);
  };

  const handleResetFilters = () => {
    dispatchFilter({ type: 'RESET_FILTERS' });
  };

  const chips = useMemo(() => filterChips(filterState.active, t), [filterState.active, t]);
  const activeFiltersCount = chips.length;

  return (
    <div className="container mx-auto py-4">
      <div className="mb-6">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl font-bold text-gray-900">{t('My tickets')}</h1>
          {/* The technician's way in to his own availability (L3b). `/profile` carries the same
              thing and is unreachable from the two L'Oréal shells in production, whose native
              header and frozen tabs would need a release to lead there. `isTechnician` is an
              exact match here — `hasRole` only walks the hierarchy on a `+`/`-` modifier — so an
              admin neither sees the pill nor triggers its request. */}
          {isTechnician && <MyAvailabilitySheet />}
        </div>
        {/* The requests segment (D23): a segment of this screen and not a route of its own — the
            native shells' four tabs are frozen and `/issues` is where a technician already lands,
            so a `/requests` route would be unreachable. */}
        {isTechnician && (
          <div className="mb-[14px] mt-3 flex w-full gap-1 rounded-xl border border-zinc-200 bg-zinc-50 p-[3px] dark:border-zinc-800 dark:bg-zinc-950">
            {/* `min-h-11` and not the mock-up's 38 px: a segment is tappable, so the hit area wins. */}
            <button
              type="button"
              aria-pressed={showRequests}
              onClick={() => handleSegmentChange('requests')}
              className={`inline-flex min-h-11 flex-1 items-center justify-center gap-[7px] rounded-[9px] text-[13.5px] transition-colors ${showRequests ? 'bg-white font-semibold text-zinc-900 shadow-[0_1px_2px_rgba(16,24,40,0.1)] dark:bg-zinc-900 dark:text-zinc-50' : 'bg-transparent font-medium text-zinc-500 dark:text-zinc-400'}`}
            >
              {t('Requests')}
              {pendingRequests > 0 && (
                <span
                  data-testid="requests-badge"
                  className={`inline-flex h-[17px] min-w-[17px] items-center justify-center rounded-md px-[5px] text-[11px] font-semibold ${requestsBadge.unseenReminders > 0 ? 'bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300' : 'bg-sky-50 text-sky-700 dark:bg-sky-950 dark:text-sky-400'}`}
                >
                  <span aria-hidden="true">{pendingRequests}</span>
                  <span className="sr-only">{pendingBadgeLabel(t, requestsBadge)}</span>
                </span>
              )}
            </button>
            <button
              type="button"
              aria-pressed={!showRequests}
              onClick={() => handleSegmentChange('tickets')}
              className={`inline-flex min-h-11 flex-1 items-center justify-center gap-[7px] rounded-[9px] text-[13.5px] transition-colors ${!showRequests ? 'bg-white font-semibold text-zinc-900 shadow-[0_1px_2px_rgba(16,24,40,0.1)] dark:bg-zinc-900 dark:text-zinc-50' : 'bg-transparent font-medium text-zinc-500 dark:text-zinc-400'}`}
            >
              {t('Tickets')}
            </button>
          </div>
        )}
        <div className="mt-2 text-sm text-gray-600">
          {isMultiSectors ? t('Sectors:') : t('Sector:')} {userSectors[0] === '*' ? t('Whole territory') :
            userSectors.length > 10
              ? `${userSectors.slice(0, 3).join(', ')}, ... , ${userSectors.slice(-3).join(', ')}`
              : userSectors.sort((a, b) => a.length - b.length || a.localeCompare(b)).join(', ')
          }
        </div>

      </div>

      {showRequests ? (
        /* One failing section degrades to a local fallback; the ticket view keeps working. */
        <Sentry.ErrorBoundary
          fallback={
            <p role="alert" className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-300">
              {t('Your requests could not be displayed. Reload the page; if it persists, the incident has been reported.')}
            </p>
          }
        >
          <MyInterventionRequests />
        </Sentry.ErrorBoundary>
      ) : (
      <>
      {/* Segmented control Mes demandes / Autres tickets — only when both kinds exist */}
      {showOwnerToggle && (
        <div className="mb-4 flex w-full sm:inline-flex sm:w-auto rounded-lg border border-gray-200 p-0.5 bg-gray-50">
          <button
            className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${ownerScope === 'mine' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setOwnerScope('mine')}
          >
            {t('My requests')}
          </button>
          <button
            className={`flex-1 sm:flex-none px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${ownerScope === 'others' ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            onClick={() => setOwnerScope('others')}
          >
            {t('Other tickets')}
          </button>
        </div>
      )}

      {/* Search and filter button */}
      <div className="mb-4 flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <TextField.Root
            size="3"
            placeholder={t('Search…')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          >
            <TextField.Slot>
              <MagnifyingGlassIcon height="16" width="16" />
            </TextField.Slot>
            {searchQuery && (
              <TextField.Slot>
                <button
                  onClick={() => setSearchQuery('')}
                  className="hover:bg-gray-200 rounded-full p-1 transition-colors"
                  aria-label={t('Clear search')}
                >
                  <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </TextField.Slot>
            )}
          </TextField.Root>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          <Modal
            open={isFilterModalOpen}
            onOpenChange={setIsFilterModalOpen}
            onApply={handleApplyFilters}
            onCancel={handleCancelFilters}
            onEscapeKeyDown={(event) => {
              if (event.target?.closest?.('[data-list-open]')) event.preventDefault();
            }}
            title={t('Filters')}
            description={t('Filters apply to every tab and stay on until you clear them.')}
            trigger={
              <Button
                size="3"
                onClick={handleOpenFilterModal}
                className={`shrink-0 ${activeFiltersCount > 0
                  ? '!bg-black !text-white hover:!bg-gray-800'
                  : '!border !border-gray-300 !bg-white !text-gray-900 hover:!bg-gray-50'}`}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"></path>
                </svg>
                {t('Filters')}
                {activeFiltersCount > 0 && (
                  <span className="ml-2 bg-white text-black rounded-full px-2 py-0.5 text-xs font-medium">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
            }
          >
            <div className="flex flex-col gap-4">
              {/* Tab choice — phones only, the tab buttons cover wider screens */}
              <div className="space-y-2 sm:hidden">
                <label className="text-sm font-medium text-gray-700">{t('Status')}</label>
                <Select.Root value={tempTab} onValueChange={setTempTab}>
                  <Select.Trigger style={{ width: '100%' }} aria-label={t('Status')} />
                  <Select.Content>
                    {tabs.map((tab) => (
                      <Select.Item key={tab.key} value={tab.key}>
                        {withCount(tab)}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select.Root>
              </div>

              {/* Client filter — only useful on a cross-client list */}
              {availableClients.length > 1 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Client')}</label>
                  <Select.Root
                    value={filterState.temp.client || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'client',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} aria-label={t('Client')} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableClients.map(client => (
                          <Select.Item key={client} value={client}>
                            {client}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Brand filter — shown when a loaded ticket or a remembered filter carries a brand */}
              {brandOptions.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Brand')}</label>
                  <MultiSelect
                    options={brandOptions.map((brand) => ({ value: brand, label: brand }))}
                    selected={filterState.temp.brands}
                    onChange={(value) => dispatchFilter({ type: 'SET_TEMP_FILTER', field: 'brands', value })}
                    placeholder={t('All brands')}
                    aria-label={t('Brand')}
                    searchable={brandOptions.length > 10}
                  />
                </div>
              )}

              {/* Filtre par priorité */}
              {availablePriorities.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Priority')}</label>
                  <Select.Root
                    value={filterState.temp.priority || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'priority',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                        {availablePriorities.map(priority => (
                          <Select.Item key={priority} value={priority}>
                            {priority}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par requestType */}
              {availableRequestTypes.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Type')}</label>
                  <Select.Root
                    value={filterState.temp.requestType || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'requestType',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableRequestTypes.map(requestType => (
                          <Select.Item key={requestType} value={requestType}>
                            {requestType}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par enseigne */}
              {availableEnseignes.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Banner')}</label>
                  <Select.Root
                    value={filterState.temp.enseigne || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'enseigne',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                        {availableEnseignes.map(enseigne => (
                          <Select.Item key={enseigne} value={enseigne}>
                            {enseigne}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par magasin */}
              {availableShops.length > 0 && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">{t('Store')}</label>
                  <Select.Root
                    value={filterState.temp.shop || "all"}
                    onValueChange={(value) => dispatchFilter({
                      type: 'SET_TEMP_FILTER',
                      field: 'shop',
                      value: value === "all" ? null : value
                    })}
                  >
                    <Select.Trigger style={{ width: '100%' }} />
                    <Select.Content>
                      <Select.Group>
                        <Select.Item value="all">{t('All (masculine)')}</Select.Item>
                        {availableShops.map(shop => (
                          <Select.Item key={shop} value={shop}>
                            {shop}
                          </Select.Item>
                        ))}
                      </Select.Group>
                    </Select.Content>
                  </Select.Root>
                </div>
              )}

              {/* Filtre par duedate */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">{t('Due date')}</label>
                <Select.Root
                  value={filterState.temp.duedate || "all"}
                  onValueChange={(value) => dispatchFilter({
                    type: 'SET_TEMP_FILTER',
                    field: 'duedate',
                    value: value === "all" ? null : value
                  })}
                >
                  <Select.Trigger style={{ width: '100%' }} />
                  <Select.Content>
                    <Select.Group>
                      <Select.Item value="all">{t('All (feminine)')}</Select.Item>
                      <Select.Item value="today">{t('Today')}</Select.Item>
                      <Select.Item value="thisWeek">{t('This week')}</Select.Item>
                      <Select.Item value="thisMonth">{t('This month')}</Select.Item>
                      <Select.Item value="overdue">{t('Overdue')}</Select.Item>
                    </Select.Group>
                  </Select.Content>
                </Select.Root>
              </div>
            </div>
          </Modal>
        </div>
      </div>

      <IssuesTabs className="mb-4" tabs={tabs} activeTab={currentTab} onChange={handleTabChange} />

      <ActiveFilterChips
        chips={chips}
        onOpen={handleOpenFilterModal}
        onRemove={(field) => dispatchFilter({ type: 'CLEAR_FILTER', field })}
        onClear={handleResetFilters}
        tabChip={tabChip}
      />

      <div>
        {loading && issues.length === 0 ? (
          <SkeletonList numberOfItems={6} />
        ) : error && issues.length === 0 ? (
          <ErrorDisplay error={error} onRetry={() => (currentTab === 'closed' ? refetchClosed() : refetchOpen())} />
        ) : (!issues || issues.length === 0) ? (
          activeFiltersCount > 0 ? (
            <>
              <EmptyState
                message={t('No ticket matches your filters')}
                description={t('Filters apply to every tab and stay on until you clear them.')}
              />
              <div className="flex justify-center">
                <Button size="2" onClick={handleResetFilters} className="!bg-black !text-white hover:!bg-gray-800">
                  {t('Clear filters')}
                </Button>
              </div>
            </>
          ) : (
            <EmptyState
              message={t('No ticket found')}
              description={currentTab === 'closed' ? t('No ticket has been closed yet.') : t('No ticket in progress.')}
            />
          )
        ) : (
          <div className={`space-y-2 ${!deviceIsInWebView ? 'pb-20' : ''}`}>
            {issues.sort((a, b) => new Date(b.fields.updated) - new Date(a.fields.updated)).map((issue) => {
              const statusColor = issue.fields.statusCategory?.colorName || getStatusColor(issue.fields.status);

              const requestTypeColor = getRequestTypeColor(issue.mappedFields.requestType?.requestType);

              return (
                <div key={issue.key} className="space-y-2">
                  <IssueCard
                    color={requestTypeColor}
                    issueKey={issue.key}
                    createdAt={issue.fields.created}
                    dueDate={getInterventionDate(issue)}
                    status={issue.fields.status.name}
                    statusColor={statusColor}
                    summary={issue.mappedFields.title}
                    interventionPoint={issue.mappedFields.intervention_point}
                    onClick={() => handleNavigateToIssue(issue.key, issue)}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default IssuesList;
